/**
 * Plan Limits Configuration
 * Defines limits and features for each subscription tier
 */

export type PlanName = 'free' | 'pro' | 'studio' | 'agency' | 'enterprise';

/**
 * Daily-cap overshoot grace for the FREE tier. One allowance per UTC day, per user.
 *
 * Mirrors the paid-tier monthly 5% grace (src/lib/billing/gate.ts:157-161) so the
 * free tier isn't held to a harsher standard than paid. Sized for the most common
 * real-world failure: a podcaster on 28/30 min uploading a ~3-4 min file. With
 * 5 min, ALL of those slip through once a day; abuse is bounded at 35 min/day.
 *
 * Consumed flag stored on users.daily_grace_consumed_at — compared by UTC date.
 */
export const GRACE_MINUTES = 5;

/**
 * Free-tier public constants — the single source of truth for anything that
 * both ENFORCES a limit and DISPLAYS it.
 *
 * These three numbers previously lived in three unrelated places: the daily cap
 * in PLAN_LIMITS, the per-file cap only in an env var read by admission.ts, and
 * a hand-typed marketing string in pricing-data.ts. They drifted, and the
 * per-file cap was never shown to users at all.
 *
 * NOTE: these are read at module load. Both are env-overridable at runtime for
 * the gate itself (env.FREE_TIER_*), so treat these as the display/default
 * values — the gate always reads env directly.
 */
export const FREE_MINUTES_PER_DAY = 30;

export const FREE_MAX_FILE_MINUTES = Number(
  process.env.FREE_TIER_MAX_FILE_DURATION_MINUTES || 30
);

export const FREE_WELCOME_FILE_MAX_MINUTES = Number(
  process.env.FREE_TIER_WELCOME_FILE_MAX_MINUTES || 180
);

/**
 * Max upload size, shared by the uploader component and the upload page.
 * 10GB — matches the value the uploader has shipped with; centralised here so
 * the two call sites can't drift (they previously hardcoded it separately).
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024 * 1024;

/**
 * Second-chance bonus minutes granted 24h after first `paywall_hit` (no conversion).
 * Auto-consumed by the gate to clear an overshoot before rejecting — see
 * src/lib/billing/bonus.ts. Expires 7 days after grant.
 */
export const SECOND_CHANCE_BONUS_MINUTES = 30;

export const SECOND_CHANCE_BONUS_EXPIRY_DAYS = 7;

/**
 * Reverse-trial config — the single dial for the no-card "Studio for 7 days"
 * offer. Time + meter, whichever trips first (see effective-plan.ts / trial.ts).
 *
 * COGS guardrail: worst-case ElevenLabs spend per granted trial is
 * `premiumMinutesCap × Scribe_per_min` (≈ 150 × ~$0.006 ≈ <$1). The 7-day clock
 * is NOT the protection — the meter is. To run the credit-only variant (no
 * countdown, "150 premium minutes that don't expire"), set `days` very large.
 */
// SAFETY: The surrounding typed contract establishes the asserted representation.
export const TRIAL = {
  /**
   * Master kill-switch for the no-card reverse trial. When false, the offer is
   * dark everywhere (banner + AI paywall hide it, startTrial refuses) — Studio's
   * "try it" falls back to Free + paywall (and the Polar card-trial, if enabled).
   * Default OFF until there's enough trial volume to A/B test no-card vs card.
   */
  enabled: false,
  /** Effective plan the trial unlocks. Must be a key of PLAN_LIMITS. */
  plan: 'studio' as PlanName,
  /** Clock kill-switch, in days. */
  days: 7,
  /** Meter kill-switch — premium STT minutes (ElevenLabs) the trial may consume. */
  premiumMinutesCap: 150,
  /** Meter kill-switch — premium TTS characters the trial may consume. */
  premiumCharsCap: 10_000,
  /** Only grant after the user's first completed transcription (cuts bot burn). */
  requireActivationFirst: true,
  /** Require OAuth or a Clerk-verified email (kills disposable-email scripting). */
  requireOAuthOrVerified: true,
  /** Max trials granted to one /24 IP ring within 24h. */
  ipRing24hMax: 3,
} as const;

export interface PlanLimits {
  minutesPerMonth: number;
  /** Free tier only: cap per calendar day. Replaces monthly for quota enforcement. */
  minutesPerDay?: number;
  ttsCharactersPerMonth: number;
  translationCharactersPerMonth: number;
  premiumVoiceCap?: number; // Percentage cap (10, 25, 35)
  // ── YouTube channel sync + searchable library (creator OS) ──────────────────
  /** 'none' | 'teaser' (list all + transcribe 1 as a taste) | 'full' (gated by pool/meter). */
  channelSync: 'none' | 'teaser' | 'full';
  /** Max distinct channels a user may sync. */
  maxChannels: number;
  /** Ceiling on videos listed/queued per single sync. */
  maxVideosPerSync: number;
  /** One-time back-catalog transcription pool (minutes), separate from the monthly meter. */
  backCatalogMinutes: number;
  /** Per-user semantic library search surface. */
  librarySearch: boolean;
  /** Turbo (ElevenLabs + priority) included within monthly minutes; else PAYG surcharge. */
  turboIncluded: boolean;
  /** Stored-media cap in GB (downloaded source audio). */
  storageGb: number;
  /** Max simultaneous in-flight intake downloads per user (anti-starvation). */
  maxConcurrentIntake: number;
  features: {
    aiSummaries: boolean; // umbrella flag for all AI tabs: Summary, Chat, Action Points, Chapters, Minutes
    speakerDiarization: boolean;
    batchUpload: boolean;
    apiAccess: boolean;
    teamCollaboration: boolean;
    customVocabulary: boolean;
    translation: boolean; // Translate to 134+ languages (gated 2026-06-04)
    /** Self-hosted Attendee meeting bot (Zoom/Meet/Teams). Studio+ — per-minute COGS. */
    meetingBot: boolean;
    /** Max simultaneous active meeting bots for this user. 0 = no access. */
    maxConcurrentMeetingBots: number;
    /**
     * Max connected calendar accounts (Google + Outlook combined) for auto-schedule.
     * Near-zero COGS (OAuth tokens only — actual spend is gated by
     * maxConcurrentMeetingBots + minutesPerMonth), so this tracks realistic team
     * size per tier rather than acting as a scarcity lever. 0 = no access.
     */
    maxConnectedCalendarAccounts: number;
    exportFormats: string[]; // free: ['txt','srt']; paid: + vtt, docx, md, json
    watermark: boolean;
  };
}

export const PLAN_LIMITS: Record<PlanName, PlanLimits> = {
  free: {
    minutesPerMonth: 60, // Legacy; free tier enforced by minutesPerDay
    minutesPerDay: FREE_MINUTES_PER_DAY,
    ttsCharactersPerMonth: 5000,
    translationCharactersPerMonth: 10000,
    channelSync: 'teaser', // list the whole channel + transcribe 1 as a taste
    maxChannels: 1,
    maxVideosPerSync: 2000, // list-only ceiling; transcription gated to 1 for teaser
    backCatalogMinutes: 0,
    librarySearch: false,
    turboIncluded: false,
    storageGb: 1,
    maxConcurrentIntake: 1,
    features: {
      aiSummaries: false,
      speakerDiarization: false,
      batchUpload: false,
      apiAccess: false,
      teamCollaboration: false,
      customVocabulary: false,
      translation: false,
      meetingBot: false,
      maxConcurrentMeetingBots: 0,
      maxConnectedCalendarAccounts: 0,
      exportFormats: ['txt', 'srt'],
      watermark: true,
    },
  },
  pro: {
    minutesPerMonth: 1000,
    ttsCharactersPerMonth: 25000,
    translationCharactersPerMonth: 50000,
    premiumVoiceCap: 10,
    channelSync: 'full',
    maxChannels: 1,
    maxVideosPerSync: 100,
    backCatalogMinutes: 1000,
    librarySearch: true,
    turboIncluded: false, // PAYG surcharge for Turbo
    storageGb: 25,
    maxConcurrentIntake: 2,
    features: {
      aiSummaries: true,
      speakerDiarization: true,
      batchUpload: false,
      apiAccess: false,
      teamCollaboration: false,
      customVocabulary: false,
      translation: true,
      meetingBot: false,
      maxConcurrentMeetingBots: 0,
      maxConnectedCalendarAccounts: 0,
      exportFormats: ['txt', 'srt', 'vtt', 'docx', 'md', 'json'],
      watermark: false,
    },
  },
  studio: {
    minutesPerMonth: 3000,
    ttsCharactersPerMonth: 90000,
    translationCharactersPerMonth: 150000,
    premiumVoiceCap: 25,
    channelSync: 'full',
    maxChannels: 3,
    maxVideosPerSync: 500,
    backCatalogMinutes: 3000,
    librarySearch: true,
    turboIncluded: true,
    storageGb: 100,
    maxConcurrentIntake: 4,
    features: {
      aiSummaries: true,
      speakerDiarization: true,
      batchUpload: true,
      apiAccess: false,
      teamCollaboration: false,
      customVocabulary: false,
      translation: true,
      meetingBot: true,
      maxConcurrentMeetingBots: 2,
      maxConnectedCalendarAccounts: 3,
      exportFormats: ['txt', 'srt', 'vtt', 'docx', 'md', 'json'],
      watermark: false,
    },
  },
  agency: {
    minutesPerMonth: 5000,
    ttsCharactersPerMonth: 150000,
    translationCharactersPerMonth: 250000,
    premiumVoiceCap: 35,
    channelSync: 'full',
    maxChannels: 10,
    maxVideosPerSync: 2000,
    backCatalogMinutes: 10000,
    librarySearch: true,
    turboIncluded: true,
    storageGb: 500,
    maxConcurrentIntake: 8,
    features: {
      aiSummaries: true,
      speakerDiarization: true,
      batchUpload: true,
      apiAccess: true,
      teamCollaboration: true,
      customVocabulary: true,
      translation: true,
      meetingBot: true,
      maxConcurrentMeetingBots: 5,
      maxConnectedCalendarAccounts: 8,
      exportFormats: ['txt', 'srt', 'vtt', 'docx', 'md', 'json'],
      watermark: false,
    },
  },
  enterprise: {
    minutesPerMonth: Infinity, // Unlimited
    ttsCharactersPerMonth: Infinity, // Unlimited
    translationCharactersPerMonth: Infinity, // Unlimited
    channelSync: 'full',
    maxChannels: Infinity,
    maxVideosPerSync: Infinity,
    backCatalogMinutes: Infinity,
    librarySearch: true,
    turboIncluded: true,
    storageGb: Infinity,
    maxConcurrentIntake: Infinity,
    features: {
      aiSummaries: true,
      speakerDiarization: true,
      batchUpload: true,
      apiAccess: true,
      teamCollaboration: true,
      customVocabulary: true,
      translation: true,
      meetingBot: true,
      maxConcurrentMeetingBots: 10,
      maxConnectedCalendarAccounts: 25,
      exportFormats: ['txt', 'srt', 'vtt', 'docx', 'md', 'json'],
      watermark: false,
    },
  },
};

/**
 * Get plan limits for a given plan name
 */
export function getPlanLimits(plan: PlanName): PlanLimits {
  return PLAN_LIMITS[plan] || PLAN_LIMITS.free;
}

/**
 * Get user's plan from their role
 * Maps user role to plan name
 */
export function getUserPlan(role: string): PlanName {
  const roleToPlan: Record<string, PlanName> = {
    user: 'free',
    pro: 'pro',
    studio: 'studio',
    agency: 'agency',
    enterprise: 'enterprise',
    admin: 'enterprise', // Admins get enterprise features
  };
  
  return roleToPlan[role.toLowerCase()] || 'free';
}

/**
 * Check if a plan has a specific feature
 */
export function hasFeature(plan: PlanName, feature: keyof PlanLimits['features']): boolean {
  const limits = getPlanLimits(plan);

  return limits.features[feature] === true;
}

